import { defineComponent, reactive, SetupContext, readonly, ref, onMounted, toRefs, watch } from '@vue/composition-api';
import CommonCategory from '@/components/common/common-content-category.component.vue';                     // カテゴリ選択共通コンポーネント
import { ContentList, TableHeader, ColumnMenu, Category } from "@/shared/type";
import { DataOptions } from 'vuetify';
import SystemConst from '@/shared/system-const';
import CommonTableFilter from '@/components/common/common-table-filter.component.vue';
import { TableFilterDefs, TableFilters } from '../common/common-table-filter.component';
import vueRouter from '@/router/index';
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';
import CommonFunction from '@/shared/common-function';
import HttpStatus from 'http-status-codes';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };                                      // Windowサイズ
    contentDisplayTableHeaders: TableHeader[];                                 // 教育用コンテンツ一覧ヘッダ情報
    contentListItems: ContentList[];                                           // 教育用コンテンツ一覧情報
    totalCount: number;                                                        // 教育用コンテンツ一覧総件数
    pageOptions: DataOptions;                                                  // ページ情報を保存する変数
    isLoading: boolean;                                                        // テーブルがロード中: true ロード中でない: false
    filterDefs: TableFilterDefs;                                               // フィルタ定義
    filters: TableFilters;                                                     // フィルタ条件
    isManagement: boolean;                                                     // 管理画面フラグ（true: 管理画面、false: 通常画面）
    publicationSettingDialog: ColumnMenu;                                      // 公開設定ダイアログ
    visibleRowMenu: ColumnMenu;                                                // 行メニュー表示切替
    categoryId: string;                                                        // カテゴリ選択で選択されたカテゴリID
    isFromDetail: boolean;                                                     // 詳細、登録、編集、削除画面からの遷移：true, それ以外からの遷移：false
    isContentRole: boolean;                                                    // 教育用コンテンツ書き込み権限あり: true, なし: false
    categoryItem: Category[];                                                  // カテゴリ情報
}

/**
 * 教育用コンテンツ一覧画面コンポーネント
 */
export default defineComponent({
    components: { CommonCategory, CommonTableFilter, CommonMessageDialog },
    setup(props, ctx: SetupContext) {
        const tableHeight = SystemConst.TABLE_HEIGHT.CONTENT_LIST;             // テーブル高さ調整用変数
        const linesPerPage = SystemConst.LIST_LINES_PER_PAGE;                  // 1ページあたりの行数候補（10, 50, 100）
        const pageNation = ref<number>(1);                                     // ページ番号（状態管理用）
        const itemsPerPage = ref<number>(linesPerPage[0]);                     // 1ページあたりの行数（状態管理用、デフォルト値：10）
        const sortBy = ref<string[]>([]);                                      // ソートカラム（状態管理用）
        const sortDesc = ref<boolean[]>([]);                                   // ソート順（状態管理用）
        let isPageLastData = false;                                            // ページの最終データフラグ
        const tableLoadingText = SystemConst.TABLE_LOADING_TEXT;               // テーブルのデータ取得中に表示するテキスト
        const router = vueRouter;                                              // ルータ
        // リアクティブな変数定義
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },                                        // ウィンドウサイズ
            contentDisplayTableHeaders: [],                                    // 教育用コンテンツ一覧ヘッダー
            contentListItems: [],                                              // 教育用コンテンツ一覧情報
            totalCount: 0,                                                     // 教育用コンテンツ一覧総件数
            pageOptions: {                                                     // ページ情報を保存する変数
                page: 1, itemsPerPage: linesPerPage[0], sortBy: [], sortDesc: [],
                groupBy: [], groupDesc: [], multiSort: false, mustSort: false
            },
            isLoading: false,                                                  // テーブルがロード中: true ロード中でない: false
            filterDefs: {},                                                    // フィルタ定義
            filters: {},                                                       // フィルタ条件
            isManagement: false,                                               // 管理画面フラグ
            publicationSettingDialog: {},                                      // 公開設定ダイアログ
            visibleRowMenu: {},                                                // 行メニュー表示切替
            categoryId: '1',                                                   // カテゴリ選択で選択されたカテゴリID
            isFromDetail: false,                                               // 詳細、登録、編集、削除画面からの遷移：true, それ以外からの遷移：false
            isContentRole: false,                                              // 教育用コンテンツ書き込み権限あり: true, なし: false
            categoryItem: []                                                   // カテゴリ情報
        });
        const tableHeaderClass: string[] = ['tableHeader subtitle-2'];         // 教育用コンテンツ一覧のヘッダ色
        const categoryRef = ref<any>(null);                                    // カテゴリー選択
        const filterRef = ref<any>(null);                                      // フィルタコンポーネント
        const commonRef = ref<any>(null);                                      // メッセージダイアログ（HTTP通信失敗のエラー画面遷移用）

        // 教育用コンテンツ一覧ヘッダ情報
        const contentInitialDisplayTableHeaders: TableHeader[] = [
            {
                text: '',
                value: 'isNew',
                sortable: false,
                width: '4%',
                class: tableHeaderClass
            },
            {
                text: 'タイトル',
                value: 'title',
                width: '81%',
                class: tableHeaderClass
            },
            {
                text: '更新日',
                value: 'updateDate',
                width: '15%',
                class: tableHeaderClass
            }
        ];
        // 教育用コンテンツ一覧（管理）ヘッダ情報
        const contentManagementInitialDisplayTableHeaders: TableHeader[] = [
            {
                text: 'タイトル',
                value: 'title',
                width: '48%',
                class: tableHeaderClass
            },
            {
                text: '公開範囲',
                value: 'publicationRangeName',
                width: '20%',
                class: tableHeaderClass
            },
            {
                text: '公開状態',
                value: 'publicState',
                width: '15%',
                class: tableHeaderClass
            },
            {
                text: '更新日',
                value: 'updateDate',
                width: '13%',
                class: tableHeaderClass
            },
            // 行アイコン
            {
                text: '',
                value: 'icons',
                sortable: false,
                width: '4%',
                class: tableHeaderClass,
                align: 'end'
            }
        ];

        // フィルタ項目
        const filterDefs: TableFilterDefs = {
            'freeWord': { title: 'フリーワード', types: 'FreeWord' },
            'updateDate': { title: '更新日', types: 'DatePeriod' }
        };

        // フィルタ項目（管理）
        const managementFilterDefs: TableFilterDefs = {
            'freeWord': { title: 'フリーワード', types: 'FreeWord' },
            'publicationRangeName': { title: '公開範囲', types: 'CheckBox' },
            'publicState': { title: '公開状態', types: 'CheckBox' },
            'updateDate': { title: '更新日', types: 'DatePeriod' }
        };

        // フィルタバリデーション
        // フリーワード
        const freeWordRules = [
            (v: any) => CommonFunction.checkLength(
                v,
                SystemConst.CONTENT_MAX_LENGTH.FREE_WORD,
                'フリーワード'
            )
        ];

        // カテゴリ選択のアクティブノードを監視する
        const { activeNode } = toRefs(ctx.root.$store.state.content);

        // カテゴリが選択された場合
        watch(activeNode, () => {
            // storeのアクティブノードからカテゴリーIDをstateへ保存する
            state.categoryId = ctx.root.$store.state.content.activeNode[0];

            // 詳細、登録、編集、削除画面以外から遷移してきた場合
            if (!state.isFromDetail) {
                // 一般モードに設定
                state.isManagement = false;

                // 管理フラグをstoreに保存
                ctx.root.$store.commit('content/setIsManagement', state.isManagement);

                // ヘッダ設定
                state.contentDisplayTableHeaders = contentInitialDisplayTableHeaders;

                // state.filterDefsにfilterDefsを設定する
                state.filterDefs = filterDefs;

                // ページ情報/ソート情報、フィルタ条件を初期化する
                pageNation.value = 1;
                itemsPerPage.value = 10;
                sortBy.value = [];
                sortDesc.value = [];
                state.filters = {};

                // 表示されているフィルタ条件を削除する
                filterRef.value.allClear();
            }
            // state.filtersを初期化した際に、updateFilters()が起動し一覧を更新する

            // 詳細、登録、編集、削除画面からの遷移フラグにfalseを設定
            // 詳細、登録、編集、削除画面から遷移してきた場合、trueのままではカテゴリ選択時に一覧更新されないため
            state.isFromDetail = false;
        })

        /**
         * 初期処理
         */
        beforeCreate();
        async function beforeCreate() {
            // storeから管理画面フラグを復元
            state.isManagement = ctx.root.$store.state.content.isManagement;
            if (state.isManagement) {
                // 管理画面フラグがtrueの場合
                //   state.filterDefsにmanagementFilterDefsを設定する
                state.filterDefs = managementFilterDefs;
            } else {
                // そうでない場合
                //   state.filterDefsにfilterDefsを設定する
                state.filterDefs = filterDefs;
            }
        }

        /**
         * 初期処理
         */
        onMounted(() => {
            init();
        });
        async function init() {
            // 教育用コンテンツ書き込み権限取得処理呼び出し
            await getContentRole();

            // カテゴリ取得
            categoryRef.value.getCategory();

            // 初期表示時にカテゴリ選択のサイズ調整
            onResize();
        }

        /**
         * 教育用コンテンツ書き込み権限取得処理
         */
        async function getContentRole() {
            // 教育用コンテンツ書き込み権限取得APIを呼び出す
            // パラメータ: なし
            const response = await ctx.root.$store.dispatch('content/getContentRole');

            // 通信結果判別
            if (response.status !== HttpStatus.OK) {
                // HTTPステータスコードが200以外の場合
                // エラー画面へ遷移して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // ストアの認証ユーザー情報取得
            const loginUser = ctx.root.$store.state.authenticationUser;

            // 認証ユーザー情報の教育用コンテンツ書き込み権限に取得した権限を設定する
            loginUser.role.isContent = response.data.isContent;

            // 認証ユーザー情報をストアに保存
            ctx.root.$store.commit('setAuthenticationUser', loginUser);
            
            // storeファイルから教育用コンテンツ書き込み権限を取得し、state.isContentRoleに設定する
            // 管理モード切り替えボタンの判別に用いる
            state.isContentRole = ctx.root.$store.state.authenticationUser.role.isContent;
        }

        /**
         * 初期表示
         */
        created();
        async function created() {
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-format-list-bulleted');

            // mainTitle変更
            ctx.root.$store.commit('setMainTitle', '教育用コンテンツ一覧');

            // storeからカテゴリ選択で選択された組織IDを復元
            state.categoryId = ctx.root.$store.state.content.activeNode[0];

            // storeからフィルター条件をすべて復元
            state.filters = ctx.root.$store.state.content.filters;

            // storeから前回のページ番号、1ページあたりの行数を復元
            pageNation.value = ctx.root.$store.state.content.pageNation;
            itemsPerPage.value = ctx.root.$store.state.content.itemsPerPage;

            // storeから前回のソートキー、ソート順を復元
            sortBy.value = ctx.root.$store.state.content.sortBy;
            sortDesc.value = ctx.root.$store.state.content.sortDesc;

            // 詳細、登録、編集、削除画面からの遷移フラグを復元
            state.isFromDetail = ctx.root.$store.state.content.isFromDetail;

            // storeからページの最終データフラグを復元
            isPageLastData = ctx.root.$store.state.content.isPageLastData;
            // storeのページの最終データフラグをfalseに設定
            ctx.root.$store.commit('content/setIsPageLastData', false);
            
            // isPageLastDataがtrueの場合、ページ番号を-1する
            if (isPageLastData) {
                pageNation.value -= 1;
                state.pageOptions.page -= 1;
            }

            // フィルタ情報を初期設定
            // awaitで処理の完了を待つ
            await initFilter();

            // ヘッダ設定
            if (state.isManagement) {
                // 管理画面フラグがtrueの場合
                state.contentDisplayTableHeaders = contentManagementInitialDisplayTableHeaders;
            } else {
                // そうでない場合
                state.contentDisplayTableHeaders = contentInitialDisplayTableHeaders;
            }

            // 前回のフィルタに対するID格納用の配列
            const filterIdList = [];
            // 前回のフィルタ条件格納用の配列
            const filterParameters = [];

            // state.filtersのプロパティ(key)で以下を繰り返し、有効なフィルタだけを抽出
            for (const key in state.filters) {
                // state.filtersのプロパティにkeyがある場合
                if (Object.prototype.hasOwnProperty.call(state.filters, key)) {
                    const val = state.filters[key];
                    // 有効なフィルタである場合
                    if (val.isEnabled) {
                        // フィルタのID(key)とフィルタ条件を保存
                        filterIdList.push(key);
                        filterParameters.push(val.condition);
                    }
                }
            }

            // 復元された有効なフィルタがある場合
            if (filterIdList.length !== 0) {
                // setAllFiltersで有効なフィルタをすべてstateに設定
                filterRef.value.setAllFilters(filterIdList, filterParameters);
            } else {
                // なければ教育用コンテンツ一覧情報を設定
                updateList();
            }

            // 詳細、登録、編集、削除からの遷移フラグにfalseを設定
            state.isFromDetail = false;
            ctx.root.$store.commit('content/setToDetailFlg', false);
        }

        /**
         * フィルタ情報を取得・設定
         */
        async function initFilter() {
            // 教育用コンテンツ一覧フィルタ情報APIでフィルタに表示する情報を取得
            // GET /api/content/filter-conditions
            // パラメータ：なし
            const response = await ctx.root.$store.dispatch('content/getContentFilter');

            // API通信が正常でない場合
            if (response.status !== HttpStatus.OK) {
                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);

                return;
            }

            // フィルタ項目（管理）の以下の項目にAPIで取得したフィルタ情報を設定
            // 公開範囲
            managementFilterDefs['publicationRangeName'].chkItems = response.data.publicationRangeName;
            // 公開状態
            managementFilterDefs['publicState'].chkItems = response.data.publicState;

            // フィルタ項目のフリーワードにフィルタバリデーションを設定
            filterDefs['freeWord'].validator = freeWordRules;
            // フィルタ項目（管理）のフリーワードにフィルタバリデーションを設定
            managementFilterDefs['freeWord'].validator = freeWordRules;
        }

        /**
         * 教育用コンテンツ一覧の表示更新
         */
        async function updateList() {
            // ストアに保存されているカテゴリ情報を取得する
            state.categoryItem = ctx.root.$store.state.category.contentCategory;

            // 画面の初期表示時にカテゴリ取得前であるため
            // カテゴリIDが格納されていない場合、以降の処理を行わない
            // または、カテゴリ情報に選択中のカテゴリIDが存在しない場合も以降の処理を行わない
            if (!state.categoryId
                ||
                !state.categoryItem.map(item => item['id']).includes(state.categoryId)
            ) {
                return
            }

            // テーブルがロード中のフラグをtrueに設定する
            state.isLoading = true;

            // ページの最終データフラグをfalseに設定する
            isPageLastData = false;

            // 現在のページ情報/ソート情報、フィルタ条件をストアに保存
            ctx.root.$store.commit('content/setPageNation', pageNation.value);
            ctx.root.$store.commit('content/setItemsPerPage', itemsPerPage.value);
            ctx.root.$store.commit('content/setFilters', state.filters);
            ctx.root.$store.commit('content/setSortBy', sortBy.value);
            ctx.root.$store.commit('content/setSortDesc', sortDesc.value);

            // makeListParametersでAPIパラメータを作成する
            const params = CommonFunction.makeListParameters(state.pageOptions, state.filters);

            // 教育用コンテンツ一覧取得APIでデータを取得する
            // GET /api/content
            // パラメータ：カテゴリID
            //            管理モードフラグ
            //            ページ番号
            //            1ページあたりの行数
            //            ソートキー
            //            ソート順
            //            フィルタ条件
            const response = await ctx.root.$store.dispatch('content/getContent',
                {
                    categoryId: state.categoryId,
                    isManagement: Number(state.isManagement),
                    page: params['page'],
                    itemsPerPage: params['itemsPerPage'],
                    sortKey: params['sortKey'],
                    sortOrder: params['sortOrder'],
                    filterParams: params['filterParams']
                }
            );

            // API通信が正常か
            if (response.status !== HttpStatus.OK) {
                // テーブルがロード中のフラグをfalseに設定する
                state.isLoading = false;

                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 教育用コンテンツ一覧のデータをstate.contentListItemsへ設定する
            state.contentListItems = response.data.contentLists;

            // 総検索件数をstate.totalCountへ設定する
            state.totalCount = response.data.totalCount;

            // 取得件数が1件かつ総データ件数が1件より多い場合、isPageLastDataをtrueにする
            if (state.contentListItems.length === 1 && state.totalCount > 1) {
                isPageLastData = true;
            }

            // テーブルがロード中のフラグをfalseに設定する
            state.isLoading = false;
        }

        /**
         * ページ情報/ソート情報変更に対するイベント
         * @param options ページ情報/ソート情報
         */
        function updateOptions(options: DataOptions) {
            // ページ情報をstateへ保存する
            state.pageOptions = options;

            // 状態管理用変数とoptionsの中身と比較して、いずれも変更されていなかった場合
            if (pageNation.value === options.page &&
                itemsPerPage.value === options.itemsPerPage &&
                sortBy.value === options.sortBy &&
                sortDesc.value === options.sortDesc
            ) {
                // 処理を終了する
                return;
            }

            // ページ番号、1ページあたりの行数を設定する
            pageNation.value = options.page;
            itemsPerPage.value = options.itemsPerPage;

            // ソートキー、ソート順を設定する
            sortBy.value = options.sortBy;
            sortDesc.value = options.sortDesc;

            // 教育用コンテンツ一覧を更新する
            updateList();
        }

        /**
         * フィルタ設定によるイベント
         * 
         * @param filters フィルタ条件
         */
        function updateFilters(filters: TableFilters) {
            // filtersをstateへ保存する
            state.filters = filters;

            // 詳細、登録、編集、削除画面以外からの遷移時以外
            if (!state.isFromDetail) {
                // ページ番号を1へ戻す
                state.pageOptions.page = 1;
                pageNation.value = 1;
            }

            // 教育用コンテンツ一覧を更新する
            updateList();
        }

        /**
         * 管理ボタンイベント
         */
        function managementChange() {
            // 管理フラグ設定
            state.isManagement = true;

            // 管理フラグをstoreに保存
            ctx.root.$store.commit('content/setIsManagement', state.isManagement);

            // ヘッダ設定
            state.contentDisplayTableHeaders = contentManagementInitialDisplayTableHeaders;

            // state.filterDefsにmanagementFilterDefsを設定する
            state.filterDefs = managementFilterDefs;

            // filtersを初期化
            state.filters = {};

            // ページ番号を1へ戻す
            state.pageOptions.page = 1;
            pageNation.value = 1;

            // ソートを初期化
            state.pageOptions.sortBy = [];
            sortBy.value = [];
            state.pageOptions.sortDesc = [];
            sortDesc.value = [];

            // 教育用コンテンツ一覧を更新する
            updateList();
        }

        /**
         * 教育用コンテンツ一覧添付ファイルダウンロード
         * 
         * @param contentId コンテンツID
         * @param fileSequence ファイルシーケンス
         */
         async function download(contentId: string, fileSequence: number) {
            // コンテンツ一覧添付ファイルダウンロードAPIでファイルをダウンロードする
            // GET /api/content/{contentId}/{fileSequence}
            // パラメータ：コンテンツID
            //            ファイルシーケンス
            const response = await ctx.root.$store.dispatch('content/download',
                {
                    contentId: contentId,
                    fileSequence: fileSequence
                }
            );

            // HTTPステータスコードが200以外の場合、エラー画面へ遷移して処理を終了する
            if (response.status !== HttpStatus.OK) {
                // エラー画面へ遷移して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }
        }

        /**
         * 公開設定変更処理
         * 
         * @param contentId コンテンツID
         * @param isPublic 公開フラグ
         */
        async function changePublication(contentId: string, isPublic: boolean) {
            // isPublicを反転してAPI通信用にisPublicを数値に変換する（isPublicがtrue：0、isPublicがfalse：1）
            const changeIsPublic = Number(!isPublic);

            // 公開設定変更APIを呼ぶ
            //   PUT /api/content/publication-setting/{contentId}
            // パラメータ：コンテンツID
            //            公開設定変更値
            const response = await ctx.root.$store.dispatch('content/changePublication',
                {
                    contentId: contentId,
                    changeIsPublic: changeIsPublic
                }
            );

            // API通信が正常か
            if (response.status !== HttpStatus.OK) {
                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 教育用コンテンツ一覧を表示更新する
            updateList();
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
            // カテゴリー選択コンポーネントのサイズも変更
            categoryRef.value.resize(state.windowSize);
        }

        /**
         * 教育用コンテンツ削除画面へ遷移する
         * 
         * @param contentId コンテンツID
         */
        function navigationDelete(contentId: string) {
            // isPageLastDataをstoreに保存する
            ctx.root.$store.commit('content/setIsPageLastData', isPageLastData);

            // 教育用コンテンツ削除画面からの遷移フラグをtrueとする
            // 教育用コンテンツ削除画面から戻った時にページ情報/ソート情報、フィルタ条件が初期化されないようにするため
            ctx.root.$store.commit('content/setToDetailFlg', true);

            router.push('/content/delete/' + contentId);
        }

        /**
         * 教育用コンテンツ詳細画面に遷移する
         * 
         * @param contentId コンテンツID
         */
        function navigationDetail(contentId: string) {
            // 教育用コンテンツ詳細画面からの遷移フラグをtrueとする
            // 教育用コンテンツ詳細画面から戻った時にページ情報/ソート情報、フィルタ条件が初期化されないようにするため
            ctx.root.$store.commit('content/setToDetailFlg', true);

            router.push('/content/detail/' + contentId);
        }

        /**
         * 教育用コンテンツ登録画面へ遷移する
         */
        function navigationAdd() {
            // 教育用コンテンツ登録画面からの遷移フラグをtrueとする
            // 教育用コンテンツ登録画面から戻った時にページ情報/ソート情報、フィルタ条件が初期化されないようにするため
            ctx.root.$store.commit('content/setToDetailFlg', true);

            router.push('/content/add');
        }

        /**
         * 教育用コンテンツ編集画面へ遷移する
         * 
         * @param contentId コンテンツID
         */
        function navigationUpdate(contentId: string) {
            // 教育用コンテンツ編集画面からの遷移フラグをtrueとする
            // 教育用コンテンツ編集画面から戻った時にページ情報/ソート情報、フィルタ条件が初期化されないようにするため
            ctx.root.$store.commit('content/setToDetailFlg', true);

            router.push('/content/edit/' + contentId);
        }

        return {
            tableHeight,
            linesPerPage,
            pageNation,
            itemsPerPage,
            sortBy,
            sortDesc,
            tableLoadingText,
            state: readonly(state),
            categoryRef,
            filterRef,
            commonRef,
            updateOptions,
            updateFilters,
            onResize,
            managementChange,
            download,
            changePublication,
            navigationDelete,
            navigationDetail,
            navigationAdd,
            navigationUpdate
        };
    }
});
